import { useCallback, useEffect } from "react";
import { createContext, useContext, useMemo, useState } from "react";
import { mapOrderEventDTOToOrder, Order } from "@/schemas/order";
import {
  postOrdersCount,
  usePostGetAllOrders,
} from "@/api/pizza-checker/orders/orders";
import { OrdersData } from "@/api/custom-schema";
import { useDebounce } from "@/hooks/use-debounce";
import { usePermissions } from "@/hooks/use-permissions";
import { useBranchFilter } from "@/hooks/use-branch-filter";
import {
  getStoredDate,
  getStoredNumber,
  getStoredString,
  isStoredStateRecord,
  readLocalStorageState,
  StoredStateRecord,
  writeLocalStorageState,
} from "@/hooks/use-local-storage-state";

interface OrderContextType {
  orders: Order[] | undefined;
  /** the page is being (re)loaded: `orders` can be a cached copy from before new orders shifted the list */
  ordersFetching: boolean;
  /** when `orders` was loaded (ms epoch, 0 = not yet) */
  ordersUpdatedAt: number;
  refetchOrders: () => void;
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  setPerPage: (perPage: number) => void;
  totalOrders: number | undefined;
  totalPages: number | undefined;
  idFilter: string;
  setIdFilter: (ids: string) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortOrder: "asc" | "desc";
  setSortOrder: (sortOrder: "asc" | "desc") => void;
  sentAtFilterStart: Date | undefined;
  setSentAtFilterStart: (date: Date | undefined) => void;
  sentAtFilterEnd: Date | undefined;
  setSentAtFilterEnd: (date: Date | undefined) => void;
  /**
   * Position of an order in the filtered, sorted list (the number of matching orders sorted before it);
   * null when the list is sorted by something it cannot compare. `fresh` = never answered from the backend's
   * count cache.
   */
  locateOrder: (order: Order, options?: { fresh?: boolean }) => Promise<number | null>;
}

interface OrderProviderProps {
  children: React.ReactNode;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);
const ORDER_FILTER_STORAGE_KEY = "pizza-admin:order-filters:v1";

type OrderFilterState = {
  perPage: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  idFilter: string;
  sentAtFilterStart: Date | undefined;
  sentAtFilterEnd: Date | undefined;
};

const DEFAULT_ORDER_FILTER_STATE: OrderFilterState = {
  perPage: 10,
  sortBy: "sent_at",
  sortOrder: "desc",
  idFilter: "",
  sentAtFilterStart: undefined,
  sentAtFilterEnd: undefined,
};

const getStoredSortOrder = (
  record: StoredStateRecord,
  key: string,
  fallback: "asc" | "desc",
) => {
  const value = record[key];
  return value === "asc" || value === "desc" ? value : fallback;
};

const readOrderFilterState = (): OrderFilterState => {
  const storedState = readLocalStorageState<StoredStateRecord>(
    ORDER_FILTER_STORAGE_KEY,
    {},
    (storedValue) => (isStoredStateRecord(storedValue) ? storedValue : {}),
  );
  const perPage = getStoredNumber(
    storedState,
    "perPage",
    DEFAULT_ORDER_FILTER_STATE.perPage,
  );

  return {
    perPage: perPage > 0 ? perPage : DEFAULT_ORDER_FILTER_STATE.perPage,
    sortBy: getStoredString(
      storedState,
      "sortBy",
      DEFAULT_ORDER_FILTER_STATE.sortBy,
    ),
    sortOrder: getStoredSortOrder(
      storedState,
      "sortOrder",
      DEFAULT_ORDER_FILTER_STATE.sortOrder,
    ),
    idFilter: getStoredString(
      storedState,
      "idFilter",
      DEFAULT_ORDER_FILTER_STATE.idFilter,
    ),
    sentAtFilterStart: getStoredDate(
      storedState,
      "sentAtFilterStart",
      DEFAULT_ORDER_FILTER_STATE.sentAtFilterStart,
    ),
    sentAtFilterEnd: getStoredDate(
      storedState,
      "sentAtFilterEnd",
      DEFAULT_ORDER_FILTER_STATE.sentAtFilterEnd,
    ),
  };
};

export const OrderProvider = ({ children }: OrderProviderProps) => {
  const { hasPermission } = usePermissions();
  const branchFilter = useBranchFilter();
  const initialFilterState = useMemo(readOrderFilterState, []);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(initialFilterState.perPage);
  const [idFilter, setIdFilter] = useState(initialFilterState.idFilter);
  const [sortBy, setSortBy] = useState(initialFilterState.sortBy);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    initialFilterState.sortOrder,
  );
  const [sentAtFilterStart, setSentAtFilterStart] = useState<Date | undefined>(
    initialFilterState.sentAtFilterStart,
  );
  const [sentAtFilterEnd, setSentAtFilterEnd] = useState<Date | undefined>(
    initialFilterState.sentAtFilterEnd,
  );

  const currentFilterState = useMemo<OrderFilterState>(
    () => ({
      perPage,
      sortBy,
      sortOrder,
      idFilter,
      sentAtFilterStart,
      sentAtFilterEnd,
    }),
    [perPage, sortBy, sortOrder, idFilter, sentAtFilterStart, sentAtFilterEnd],
  );

  useEffect(() => {
    writeLocalStorageState(ORDER_FILTER_STORAGE_KEY, {
      ...currentFilterState,
      sentAtFilterStart: currentFilterState.sentAtFilterStart?.toISOString(),
      sentAtFilterEnd: currentFilterState.sentAtFilterEnd?.toISOString(),
    });
  }, [currentFilterState]);

  // Debounced filter values
  const debouncedIdFilter = useDebounce(idFilter, 400);
  const debouncedSentAtFilterStart = useDebounce(sentAtFilterStart, 400);
  const debouncedSentAtFilterEnd = useDebounce(sentAtFilterEnd, 400);

  useEffect(() => {
    setPage(0);
  }, [idFilter, sentAtFilterStart, sentAtFilterEnd]);

  const sentAtFilter =
    debouncedSentAtFilterStart || debouncedSentAtFilterEnd
      ? {
          ...(debouncedSentAtFilterStart
            ? {
                $gte: debouncedSentAtFilterStart.toISOString(),
              }
            : {}),
          ...(debouncedSentAtFilterEnd
            ? {
                $lte: debouncedSentAtFilterEnd.toISOString(),
              }
            : {}),
        }
      : undefined;

  const apiFilters = useMemo(() => {
    const filters = {
      ...branchFilter,
      ...(debouncedIdFilter
        ? {
            $or: debouncedIdFilter.split(",").flatMap((value) => {
              const trimmed = value.trim().replace(/^#/, "");
              const dailyCounter = Number(trimmed);

              return [
                { "data.id": { $regex: trimmed } },
                ...(Number.isFinite(dailyCounter)
                  ? [{ "data.daily_counter": dailyCounter }]
                  : []),
                { "data.formatted_id": { $regex: `^#?${trimmed}$` } },
              ];
            }),
          }
        : {}),
      ...(sentAtFilter ? { sent_at: sentAtFilter } : {}),
    };

    return filters;
  }, [branchFilter, debouncedIdFilter, sentAtFilter]);

  const {
    data,
    isFetching: ordersFetching,
    dataUpdatedAt: ordersUpdatedAt,
    refetch: refetchOrders,
  } = usePostGetAllOrders<OrdersData>(
    {
      skip: page * perPage,
      limit: perPage,
      current_cursor: null,
      sort_by: sortBy,
      sort_order: sortOrder === "asc" ? 1 : -1,
      filters: apiFilters,
    },
    {
      query: {
        enabled: hasPermission("orders") || hasPermission("orders.branch"),
      },
    },
  );

  const locateOrder = useCallback(
    async (order: Order, { fresh = false }: { fresh?: boolean } = {}) => {
      if (sortBy !== "sent_at" || !order.sent_at) return null;
      const before: Record<string, unknown> = {
        [sortOrder === "asc" ? "$lt" : "$gt"]: order.sent_at,
      };
      // a unique filter past the backend's count cache, see locatePizza in pizza-context
      if (fresh) before.$ne = `~locate ${Date.now()} ${Math.random()}`;
      const result = (await postOrdersCount({
        filters: { $and: [apiFilters, { sent_at: before }] },
      })) as { count?: number } | undefined;
      return typeof result?.count === "number" ? result.count : null;
    },
    [apiFilters, sortBy, sortOrder],
  );

  return (
    <OrderContext.Provider
      value={useMemo(
        () => ({
          orders: data?.orders.map((order) => mapOrderEventDTOToOrder(order)),
          ordersFetching,
          ordersUpdatedAt,
          refetchOrders,
          page,
          setPage,
          perPage,
          setPerPage,
          totalOrders: data?.total_orders,
          totalPages: data?.total_pages,
          idFilter,
          setIdFilter,
          sortBy,
          setSortBy,
          sortOrder,
          setSortOrder,
          sentAtFilterStart,
          setSentAtFilterStart,
          sentAtFilterEnd,
          setSentAtFilterEnd,
          locateOrder,
        }),
        [
          locateOrder,
          data,
          ordersFetching,
          ordersUpdatedAt,
          refetchOrders,
          page,
          setPage,
          perPage,
          setPerPage,
          idFilter,
          setIdFilter,
          sentAtFilterStart,
          setSentAtFilterStart,
          sentAtFilterEnd,
          setSentAtFilterEnd,
          sortBy,
          setSortBy,
          sortOrder,
          setSortOrder,
        ],
      )}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};
